import cx from "classnames";
import { ReactNode } from "react";

import { Anchor } from "@/components/elements/Anchor";
import { PageLink } from "@/types/types";

import styles from "./styles.module.css";

export type ButtonThemes =
  | "dark"
  | "light"
  | "green"
  | "outline"
  | "outline-secondary"
  | "outline-square"
  | "underline";
export type ButtonSizes = "regular" | "small" | "x-small";
export type ButtonWidths = "auto" | "full";

type ThemeProps =
  // Solid button
  {
    theme?: ButtonThemes;
    size?: ButtonSizes;
    width?: ButtonWidths;
  };

type CtaProps =
  | {
      type: "submit" | "reset";
      pageLink?: never;
      onClick?: never;
      href?: never;
    }
  | {
      onClick: (MouseEvent) => void;
      pageLink?: never;
      href?: never;
    }
  | {
      onClick?: never;
      pageLink: PageLink | string;
      href?: never;
    }
  | {
      pageLink?: never;
      onClick?: never;
      href: string;
    };

export type ButtonProps = ThemeProps &
  CtaProps & {
    type?: "button" | "submit" | "reset";
    label?: string;
    disabled?: boolean;
    loading?: boolean;
    children: ReactNode;
    className?: string;
    innerClassName?: string;
    style?: Record<string, unknown>;
  } & CtaProps;

export const Button = ({
  theme = "dark",
  size = "regular",
  width = "auto",
  type = "button",
  label,
  disabled,
  loading,
  children,
  pageLink,
  href,
  onClick = () => {},
  className,
  innerClassName,
  style,
}: ButtonProps) => {
  const containerClasses = cx(
    className,
    styles.button,
    styles[theme],
    styles[size],
    styles[width]
  );
  // If a page link, use anchor tag
  if (pageLink || href) {
    return (
      <Anchor
        {...(pageLink ? { pageLink } : { href })}
        className={containerClasses}
        label={label}
      >
        <span className={styles.inner}>{children}</span>
      </Anchor>
    );
  }
  // Else use a button
  return (
    <button
      onClick={onClick}
      aria-label={label}
      disabled={disabled || loading}
      type={type}
      className={containerClasses}
      style={style}
    >
      <span className={cx(innerClassName, styles.inner)}>{children}</span>
    </button>
  );
};
